import {ChangeDetectorRef, Component, OnInit, ViewContainerRef} from '@angular/core';
import {ConfigurationService, OdGroup} from '../configuration.service';
import {LookupService} from '../../shared/lookup.service';
import {NotificationPopupService} from '../../shared/notification-popup/services/notification-popup.service';
import {ConfigService} from '../../core/services/config/config.service';

// An origin plus the destinations that pair with it, as returned by
// /dim_lookup/origins. Driving the destination dropdown off this array is what makes a
// nonexistent OD impossible to select.
interface OriginOption {
    id: string;
    name: string;
    destinations: string[];
}

@Component({
    selector: 'app-od-grouping',
    templateUrl: './od-grouping.component.html',
    styleUrls: ['./od-grouping.component.scss']
})
export class OdGroupingComponent implements OnInit {
    // OD Groups - the parent/child OD pairs in rail.od_grouping. Unlike the other
    // sections on this screen these are rows in their own table rather than a JSON blob
    // in user_configuration, because the batch app and the inventory screens read that
    // table directly.
    //
    // Edits are staged in odGroups and posted as a whole list by Save, the same contract
    // as protected_buckets_capacity. The grid renders rows read-only and a single
    // add/edit form above it carries the dropdowns: a customer can have 200+ groups, and
    // four dropdown components per row would be several hundred of them.
    public odGroups: OdGroup[] = [];
    public odGroupsView: OdGroup[] = [];
    public odGroupFilter = '';

    // True once the initial GET has succeeded. Until then onSave() leaves the groups
    // alone rather than posting an empty list, which the API would honour as "delete
    // every group" - a failed read must not wipe the configuration.
    public odGroupsLoaded = false;

    // rail.parameters.od_grouping. When this is off the groups are still editable but
    // nothing applies them, and the screen says so rather than implying otherwise.
    // Read-only here - there is nothing in this section's own Save that changes it.
    public odGroupingEnabled = false;

    // Cascading dropdown data for the add/edit form.
    public originOptions: OriginOption[] = [];
    public parentOrigin: string;
    public parentDestination: string;
    public childOrigin: string;
    public childDestination: string;

    // Index into odGroups of the row being edited, or -1 when the form is adding.
    public editingOdGroupIndex = -1;

    // Client-side mirror of the API's validation, so a problem shows up as it is
    // introduced rather than on Save. The API re-checks and is the authority.
    public odGroupErrors: string[] = [];

    // True once the initial groups load has settled, success or failure - gates the
    // spinner vs. content swap. Origins loading and the (synchronous) od_grouping flag
    // read don't block this: they only affect the add/edit form's dropdowns, not
    // whether there is anything to show at all.
    private odGroupsSettled = false;
    public get loaded(): boolean {
        return this.odGroupsSettled;
    }

    public saving = false;

    constructor(private configurationSvc: ConfigurationService,
                private lookupSvc: LookupService,
                private notificationPopupSvc: NotificationPopupService,
                private configSvc: ConfigService,
                private viewContainerRef: ViewContainerRef,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.loadOdGroupingFlag();
        this.loadOrigins();
        this.loadOdGroups();
    }

    // rail.parameters.od_grouping arrives with the rest of the app config, so this is a
    // read of what is already cached rather than another request.
    private loadOdGroupingFlag(): void {
        const appConfig = this.configSvc.getAppConfigObject();
        this.odGroupingEnabled = Number(appConfig?.od_grouping) === 1;
    }

    private loadOrigins(): void {
        this.lookupSvc.loadOrigins().subscribe((result: any) => {
            this.originOptions = result?.data ?? [];
            this.cdr.detectChanges();
        }, error => {
            console.error(`Error retrieving origin data ${error}`);
            this.cdr.detectChanges();
        });
    }

    private loadOdGroups(): void {
        this.configurationSvc.getOdGroups().subscribe(groups => {
            this.odGroups = groups ?? [];
            this.odGroupsLoaded = true;
            this.odGroupsSettled = true;
            this.applyOdGroupFilter();
            this.odGroupErrors = this.validateOdGroups();
            this.cdr.detectChanges();
        }, error => {
            // Leaves odGroupsLoaded false, so a save will not touch the groups.
            console.error(`Error retrieving OD groups ${error}`);
            this.odGroupsSettled = true;
            this.cdr.detectChanges();
        });
    }

    // The destinations that pair with the given origin. Returns an empty list for an
    // unset or unknown origin, which leaves the destination dropdown empty rather than
    // offering every station.
    public destinationsFor(origin: string): string[] {
        return this.originOptions.find(option => option.name === origin)?.destinations ?? [];
    }

    public handleParentOriginChange(): void {
        // The previous destination almost certainly does not pair with the new origin.
        this.parentDestination = undefined;
    }

    public handleChildOriginChange(): void {
        this.childDestination = undefined;
    }

    public get odGroupFormComplete(): boolean {
        return !!(this.parentOrigin && this.parentDestination
            && this.childOrigin && this.childDestination);
    }

    public get editingOdGroup(): boolean {
        return this.editingOdGroupIndex >= 0;
    }

    // Adds the form's contents as a new group, or replaces the row being edited.
    public commitOdGroup(): void {
        if (!this.odGroupFormComplete) {
            return;
        }

        const group: OdGroup = {
            parentOrigin: this.parentOrigin,
            parentDestination: this.parentDestination,
            childOrigin: this.childOrigin,
            childDestination: this.childDestination
        };

        if (this.editingOdGroup) {
            this.odGroups = this.odGroups.map((row, index) =>
                index === this.editingOdGroupIndex ? group : row);
        } else {
            this.odGroups = [...this.odGroups, group];
        }

        this.clearOdGroupForm();
        this.applyOdGroupFilter();
        this.odGroupErrors = this.validateOdGroups();
        this.cdr.detectChanges();
    }

    public editOdGroup(group: OdGroup): void {
        this.editingOdGroupIndex = this.odGroups.indexOf(group);
        this.parentOrigin = group.parentOrigin;
        this.parentDestination = group.parentDestination;
        this.childOrigin = group.childOrigin;
        this.childDestination = group.childDestination;
        this.cdr.detectChanges();
    }

    public removeOdGroup(group: OdGroup): void {
        this.odGroups = this.odGroups.filter(row => row !== group);
        if (this.editingOdGroup) {
            // The row being edited may have shifted or gone; start over rather than
            // updating whatever now sits at that index.
            this.clearOdGroupForm();
        }
        this.applyOdGroupFilter();
        this.odGroupErrors = this.validateOdGroups();
        this.cdr.detectChanges();
    }

    public clearOdGroupForm(): void {
        this.editingOdGroupIndex = -1;
        this.parentOrigin = undefined;
        this.parentDestination = undefined;
        this.childOrigin = undefined;
        this.childDestination = undefined;
    }

    public handleFilterOdGroups(value: string): void {
        this.odGroupFilter = value ?? '';
        this.applyOdGroupFilter();
        this.cdr.detectChanges();
    }

    private applyOdGroupFilter(): void {
        const term = this.odGroupFilter.trim().toUpperCase();
        this.odGroupsView = term.length
            ? this.odGroups.filter(group =>
                this.odLabel(group.parentOrigin, group.parentDestination).includes(term)
                || this.odLabel(group.childOrigin, group.childDestination).includes(term))
            : [...this.odGroups];
    }

    public odLabel(origin: string, destination: string): string {
        return `${origin}-${destination}`;
    }

    // Mirrors validate_od_groups in the API. Kept as messages rather than structured
    // errors because this copy exists only to tell the user what to fix; the API's copy
    // is what actually gates the write.
    public validateOdGroups(): string[] {
        const errors: string[] = [];
        const parentOf: {[childOd: string]: Set<string>} = {};
        const pairs = new Set<string>();
        const duplicates = new Set<string>();
        const selfParents = new Set<string>();
        const parents = new Set<string>();
        const children = new Set<string>();

        this.odGroups.forEach(group => {
            const parentOd = this.odLabel(group.parentOrigin, group.parentDestination);
            const childOd = this.odLabel(group.childOrigin, group.childDestination);

            const pair = `${parentOd}>${childOd}`;
            if (pairs.has(pair)) {
                duplicates.add(pair);
            }
            pairs.add(pair);

            parents.add(parentOd);
            children.add(childOd);
            (parentOf[childOd] = parentOf[childOd] ?? new Set<string>()).add(parentOd);

            if (parentOd === childOd) {
                selfParents.add(parentOd);
            }
        });

        Object.keys(parentOf).filter(childOd => parentOf[childOd].size > 1).sort()
            .forEach(childOd => errors.push(
                `${childOd} is a child of more than one parent (${
                    Array.from(parentOf[childOd]).sort().join(', ')
                }). A child OD can only belong to one parent.`));

        Array.from(duplicates).sort().forEach(pair => {
            const [parentOd, childOd] = pair.split('>');
            errors.push(`${parentOd} to ${childOd} is listed more than once.`);
        });

        Array.from(selfParents).sort().forEach(od =>
            errors.push(`${od} cannot be its own child.`));

        // OD groups are one level deep - see the API controller for why a chain would be
        // mis-applied rather than rejected further down.
        Array.from(children).filter(od => parents.has(od) && !selfParents.has(od)).sort()
            .forEach(od => errors.push(
                `${od} is used as both a parent and a child. OD groups are one level deep.`));

        return errors;
    }

    public get odGroupsValid(): boolean {
        return this.odGroupErrors.length === 0;
    }

    public onSave(): void {
        // Refuse the save on an invalid OD group list rather than letting the API
        // reject it.
        this.odGroupErrors = this.validateOdGroups();
        if (this.odGroupsLoaded && !this.odGroupsValid) {
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'error',
                message: 'OD groups are not valid. Nothing was saved.'
            });
            this.cdr.detectChanges();
            return;
        }

        // A failed initial read must not let a save delete every group - the endpoint
        // replaces the whole list, so this section simply refuses to save at all rather
        // than post an empty one.
        if (!this.odGroupsLoaded) {
            return;
        }

        this.saving = true;
        this.configurationSvc.saveOdGroups(this.odGroups).subscribe(() => {
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'success',
                message: 'Configuration saved.'
            });
            this.saving = false;
        }, error => {
            console.error(`Error saving OD groups ${error}`);
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'error',
                message: 'Failed to save configuration.'
            });
            this.saving = false;
        });
    }
}
